<template>
  <footer
    data-ga-nav="footer"
    :class="{ 'is-slim': variant === 'subpage', 'is-legal': variant === 'legal' }"
  >
    <template v-if="variant === 'legal'">
      <div class="ft-top ft-top--legal">
        <div class="ft-brand">
          <a
            href="/"
            class="logo-ft"
            :aria-label="trans.get('landing.nav.home_aria')"
          >
            <img
              src="../../../../../images/viluu/landing-v2/viluu-wordmark.svg"
              :alt="trans.get('landing.common.wordmark_alt')"
              height="20"
            />
          </a>
          <p>{{ trans.get('landing.footer.tagline') }}</p>
        </div>

        <div
          v-for="col in legalCols"
          :key="col.key"
          class="ft-col"
        >
          <h5>{{ trans.get(col.titleKey) }}</h5>
          <ul>
            <li
              v-for="link in col.links"
              :key="link.labelKey"
            >
              <a
                v-if="link.cookieReopen"
                href="#"
                class="cc-reopen"
                role="button"
                @click.prevent="reopenCookieBanner"
              >{{ trans.get(link.labelKey) }}</a>
              <a
                v-else
                :href="link.href"
              >{{ trans.get(link.labelKey) }}</a>
            </li>
          </ul>
        </div>
      </div>

      <div class="ft-disclaimer">
        <p>
          {{ trans.get('landing.footer.subpage_disclaimer_prefix') }}
          <a
            href="agb"
            class="ft-agb-link"
          >
            {{ trans.get('landing.footer.subpage_disclaimer_link_agb') }}
          </a>
          {{ trans.get('landing.footer.subpage_disclaimer_suffix') }}
        </p>
      </div>
    </template>

    <template v-else-if="variant === 'subpage'">
      <div class="ft-top ft-top--subpage">
        <div class="ft-brand">
          <a
            href="/"
            class="logo-ft"
            :aria-label="trans.get('landing.nav.home_aria')"
          >
            <img
              src="../../../../../images/viluu/landing-v2/viluu-wordmark.svg"
              :alt="trans.get('landing.common.wordmark_alt')"
              height="20"
            />
          </a>
          <p>{{ trans.get('landing.footer.tagline') }}</p>
        </div>

        <div class="ft-col">
          <h5>{{ trans.get('landing.footer.subpage_col1_title') }}</h5>
          <ul>
            <li
              v-for="city in subpageCities"
              :key="city.slug"
            >
              <a :href="`dating-${city.slug}`">{{ city.label }}</a>
            </li>
          </ul>
        </div>

        <div class="ft-col">
          <h5>{{ trans.get('landing.footer.subpage_col2_title') }}</h5>
          <ul>
            <li>
              <a href="so-funktioniert">{{ trans.get('landing.footer.subpage_col2_link1') }}</a>
            </li>
            <li>
              <a href="ueber-uns">{{ trans.get('landing.footer.subpage_col2_link2') }}</a>
            </li>
            <li>
              <a href="faq">{{ trans.get('landing.footer.subpage_col2_link3') }}</a>
            </li>
          </ul>
        </div>

        <div class="ft-col">
          <h5>{{ trans.get('landing.footer.subpage_col3_title') }}</h5>
          <ul>
            <li>
              <a href="impressum">{{ trans.get('landing.footer.imprint') }}</a>
            </li>
            <li>
              <a href="agb">{{ trans.get('landing.footer.agb') }}</a>
            </li>
            <li>
              <a href="datenschutz">{{ trans.get('landing.footer.privacy') }}</a>
            </li>
            <li>
              <a href="csae">{{ trans.get('landing.footer.csae') }}</a>
            </li>
            <li>
              <a href="cmp">{{ trans.get('landing.footer.cmp') }}</a>
            </li>
            <li>
              <a
                href="#"
                class="cc-reopen"
                role="button"
                @click.prevent="reopenCookieBanner"
              >{{ trans.get('landing.footer.cookie_pref') }}</a>
            </li>
          </ul>
        </div>
      </div>

      <div class="ft-disclaimer">
        <p>
          {{ trans.get('landing.footer.subpage_disclaimer_prefix') }}
          <a
            href="agb"
            class="ft-agb-link"
          >
            {{ trans.get('landing.footer.subpage_disclaimer_link_agb') }}
          </a>
          {{ trans.get('landing.footer.subpage_disclaimer_suffix') }}
        </p>
      </div>
    </template>

    <template v-else>
      <div class="ft-top">
        <div class="ft-brand">
          <a
            href="/"
            class="logo-ft"
            :aria-label="trans.get('landing.nav.home_aria')"
          >
            <img
              src="../../../../../images/viluu/landing-v2/viluu-appicon.svg"
              :alt="trans.get('landing.common.brand_alt')"
              width="34"
              height="34"
              class="ft-brand-appicon"
            />
            <img
              src="../../../../../images/viluu/landing-v2/viluu-wordmark.svg"
              :alt="trans.get('landing.common.wordmark_alt')"
              width="50"
              height="20"
            />
          </a>
          <p>{{ trans.get('landing.footer.tagline') }}</p>
          <div class="socials">
            <a
              href="https://www.facebook.com/profile.php?id=61558796579760"
              class="soc facebook"
              data-network="facebook"
              :aria-label="trans.get('landing.footer.social_facebook')"
              target="_blank"
              rel="noopener"
            >
              <icon-facebook />
            </a>
            <a
              href="https://www.instagram.com/viluuapp/"
              class="soc instagram"
              data-network="instagram"
              :aria-label="trans.get('landing.footer.social_instagram')"
              target="_blank"
              rel="noopener"
            >
              <icon-instagram />
            </a>
            <a
              href="https://www.tiktok.com/@viluu.app"
              class="soc tiktok"
              data-network="tiktok"
              :aria-label="trans.get('landing.footer.social_tiktok')"
              target="_blank"
              rel="noopener"
            >
              <icon-tiktok />
            </a>
          </div>
        </div>

        <div
          v-for="col in cols"
          :key="col.key"
          class="ft-col"
        >
          <h5>{{ trans.get(col.titleKey) }}</h5>
          <ul>
            <li
              v-for="link in col.links"
              :key="link.labelKey"
            >
              <a
                :href="link.href"
                data-ga-nav-link
                v-bind="mailtoAttrs(link.href)"
              >{{ trans.get(link.labelKey) }}</a>
            </li>
          </ul>
        </div>
      </div>

      <div class="ft-disclaimer">
        <p>{{ trans.get('landing.footer.disclaimer_1') }}</p>
        <p>{{ trans.get('landing.footer.disclaimer_2') }}</p>
        <p>
          {{ trans.get('landing.footer.disclaimer_3_prefix') }}
          <a
            href="agb"
            class="ft-agb-link"
          >
            {{ trans.get('landing.footer.disclaimer_3_link_agb') }}
          </a>
          {{ trans.get('landing.footer.disclaimer_3_middle') }}
          <a href="/datenschutz">{{ trans.get('landing.footer.disclaimer_3_link_privacy') }}</a>
          {{ trans.get('landing.footer.disclaimer_3_suffix') }}
        </p>
      </div>
    </template>

<!--    <div-->
<!--      v-if="variant === 'full'"-->
<!--      class="ft-trust"-->
<!--    >-->
<!--      <a-->
<!--        href="https://www.jugendschutzprogramm.de/"-->
<!--        target="_blank"-->
<!--        rel="noopener"-->
<!--        :aria-label="trans.get('landing.footer.jusprog_aria')"-->
<!--        class="jusprog-link"-->
<!--      >-->
<!--        <img-->
<!--          src="../../../../../images/viluu/landing-v2/badges/jusprog.png"-->
<!--          :alt="trans.get('landing.footer.jusprog_alt')"-->
<!--          width="151"-->
<!--          height="31"-->
<!--          loading="lazy"-->
<!--          class="jusprog-img"-->
<!--        />-->
<!--      </a>-->
<!--    </div>-->

    <div
      v-if="variant === 'full'"
      class="ft-meta"
    >
      <div class="ft-address">
        <span class="pin"><icon-map-marker :stroke-width="2.2" /></span>
        <div>
          <strong>{{ trans.get('landing.footer.company_name') }}</strong>
          {{ trans.get('landing.footer.company_address') }}
        </div>
      </div>
      <div class="ft-pay">
        <span
          class="pay-card pay-visa"
          :aria-label="trans.get('landing.footer.pay_visa')"
        ><span class="visa-mark">VISA</span></span>
        <span
          class="pay-card pay-mc"
          :aria-label="trans.get('landing.footer.pay_mc')"
        >
          <span class="mc-circles"><span class="l"></span>
            <span class="r"></span></span>
        </span>
        <span
          class="pay-card pay-sofort"
          :aria-label="trans.get('landing.footer.pay_sofort')"
        >
          <img
            src="../../../../../images/viluu/landing-v2/payments/sofort.png"
            :alt="trans.get('landing.footer.pay_sofort')"
            width="196"
            height="72"
            loading="lazy"
            decoding="async"
          />
        </span>
        <span
          class="pay-card pay-paysafe"
          :aria-label="trans.get('landing.footer.pay_paysafe')"
        >
          <img
            src="../../../../../images/viluu/landing-v2/payments/paysafe.png"
            :alt="trans.get('landing.footer.pay_paysafe')"
            width="196"
            height="60"
            loading="lazy"
            decoding="async"
          />
        </span>
      </div>
    </div>

    <div class="ft-bot">
      <span>{{ copyright }}</span>
      <div class="legal">
        <a href="/impressum">{{ trans.get('landing.footer.imprint') }}</a>
        <a href="/agb">{{ trans.get('landing.footer.agb') }}</a>
        <a href="/datenschutz">{{ trans.get('landing.footer.privacy') }}</a>
        <a href="/csae">{{ trans.get('landing.footer.csae') }}</a>
        <a href="/cmp">{{ trans.get('landing.footer.cmp') }}</a>
        <a
          v-if="variant === 'full'"
          href="#"
          class="cc-reopen"
          role="button"
          @click.prevent="reopenCookieBanner"
        >{{ trans.get('landing.footer.cookie_pref') }}</a>
      </div>
    </div>
  </footer>
</template>

<script>
import { CITY_SLUGS, cityTitleKey } from '../../../views/pages/LandingV2/subpages/cities';
import IconFacebook from '../icons/IconFacebook.vue';
import IconInstagram from '../icons/IconInstagram.vue';
import IconTiktok from '../icons/IconTiktok.vue';
import IconMapMarker from '../icons/IconMapMarker.vue';

export default {
  name: 'TheFooter',
  components: { IconFacebook, IconInstagram, IconTiktok, IconMapMarker },
  props: {
    // full — landing page (5 cols + trust + payments)
    // subpage — faq / city pages (Städte + Viluu + Rechtliches)
    // legal — agb / impressum / datenschutz (Produkt + Hilfe + Rechtliches + Sicherheit)
    variant: {
      type: String,
      default: 'full',
      validator: (v) => ['full', 'subpage', 'legal'].includes(v),
    },
  },
  data() {
    return {
      legalCols: [
        {
          key: 'product',
          titleKey: 'landing.footer.col1_title',
          links: [
            { labelKey: 'landing.footer.col1_link1', href: '/so-funktioniert' },
            { labelKey: 'landing.footer.col1_link2', href: '/#quiz-match' },
            { labelKey: 'landing.footer.col1_link4', href: '/#stories' },
            { labelKey: 'landing.footer.col1_link6', href: '/gutscheincode' },
          ],
        },
        {
          key: 'help',
          titleKey: 'landing.footer.col4_title',
          links: [
            { labelKey: 'landing.footer.col4_link1', href: '/faq' },
            { labelKey: 'landing.footer.col3_link2', href: 'mailto:support@viluu.de' },
          ],
        },
        {
          key: 'legal',
          titleKey: 'landing.footer.subpage_col3_title',
          links: [
            { labelKey: 'landing.footer.imprint', href: '/impressum' },
            { labelKey: 'landing.footer.agb', href: '/agb' },
            { labelKey: 'landing.footer.privacy', href: '/datenschutz' },
            { labelKey: 'landing.footer.csae', href: '/csae' },
            { labelKey: 'landing.footer.cmp', href: '/cmp' },
            { labelKey: 'landing.footer.cookie_pref', cookieReopen: true },
          ],
        },
        {
          key: 'safety',
          titleKey: 'landing.footer.col2_title',
          links: [
            { labelKey: 'landing.footer.col2_link1', href: '/faq' },
            { labelKey: 'landing.footer.col2_link3', href: '/agb' },
            { labelKey: 'landing.footer.col2_link4', href: '/faq' },
          ],
        },
      ],
      cols: [
        {
          key: 'product',
          titleKey: 'landing.footer.col1_title',
          links: [
            { labelKey: 'landing.footer.col1_link1', href: '/so-funktioniert' },
            { labelKey: 'landing.footer.col1_link2', href: '#quiz-match' },
            { labelKey: 'landing.footer.col1_link3', href: '/faq' },
            { labelKey: 'landing.footer.col1_link4', href: '#stories' },
            { labelKey: 'landing.footer.col1_link5', href: '#top-choice' },
            { labelKey: 'landing.footer.col1_link6', href: '/gutscheincode' },
          ],
        },
        {
          key: 'safety',
          titleKey: 'landing.footer.col2_title',
          links: [
            { labelKey: 'landing.footer.col2_link1', href: '/so-funktioniert' },
            { labelKey: 'landing.footer.col2_link2', href: '/datenschutz' },
            { labelKey: 'landing.footer.col2_link3', href: '/agb' },
            { labelKey: 'landing.footer.col2_link4', href: '/faq' },
          ],
        },
        {
          key: 'company',
          titleKey: 'landing.footer.col3_title',
          links: [
            { labelKey: 'landing.footer.col3_link1', href: '/ueber-uns' },
            { labelKey: 'landing.footer.col3_link2', href: 'mailto:support@viluu.de' },
            { labelKey: 'landing.footer.col3_link3', href: '/impressum' },
          ],
        },
        {
          key: 'help',
          titleKey: 'landing.footer.col4_title',
          links: [
            { labelKey: 'landing.footer.col4_link1', href: '/faq' },
            { labelKey: 'landing.footer.col4_link2', href: '/faq' },
            { labelKey: 'landing.footer.col4_link3', href: '/faq' },
            { labelKey: 'landing.footer.col4_link4', href: 'mailto:support@viluu.de' },
          ],
        },
      ],
    };
  },
  computed: {
    copyright() {
      // Replace :year client-side so the value updates every reload without
      // having to invalidate the i18n cache once a year.
      return this.trans.get('landing.footer.copyright').replace(':year', new Date().getFullYear());
    },
    subpageCities() {
      return CITY_SLUGS.map((slug) => ({
        slug,
        label: this.trans.get(cityTitleKey(slug)),
      }));
    },
  },
  methods: {
    mailtoAttrs(href) {
      if (href && String(href).toLowerCase().startsWith('mailto:')) {
        return { 'data-ga-support': '1' };
      }
      return {};
    },
    reopenCookieBanner() {
      this.$eventHub.$emit('viluu-cookie:reopen');
    },
  },
};
</script>
<style scoped lang="scss">
footer {
  position: relative;
  z-index: 5;
  padding: 60px 56px 40px;
  max-width: 1480px;
  margin: 0 auto;
  border-top: 1px solid var(--line);
  line-height: normal;
}

.ft-top {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr 1fr;
  gap: 40px;
  margin-bottom: 50px;
}

.ft-brand .logo-ft {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  text-decoration: none;
  color: inherit;
}

.ft-brand .logo-ft:hover {
  opacity: 0.85;
}

.ft-brand .ft-brand-appicon {
  flex-shrink: 0;
  display: block;
}

.ft-brand p {
  font-size: 13px;
  color: var(--ink-dim);
  line-height: 1.55;
  max-width: 280px;
  margin: 0 0 22px;
}

.ft-brand .socials {
  display: flex;
  gap: 10px;
}

.ft-brand .soc {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--line);
  color: #fff;
}

.ft-brand .soc:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px -6px rgba(26, 12, 20, 0.25);
}

.ft-brand .soc svg {
  width: 20px;
  height: 20px;
  display: block;
  flex-shrink: 0;
}

.ft-brand .soc.facebook {
  background: #1877f2;
  border-color: #1877f2;
}

.ft-brand .soc.tiktok {
  background: #000;
  border-color: #000;
}

.ft-brand .soc.instagram {
  background: #e4405f;
  border-color: #e4405f;
}

.ft-trust {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
  flex-wrap: wrap;
  padding: 14px 0 4px;
  border-top: 1px solid var(--line);
}

.ft-trust .jusprog-link {
  display: inline-flex;
  align-items: center;
  line-height: 0;
  transition: opacity 0.2s ease;
  opacity: 0.95;
}

.ft-trust .jusprog-link:hover {
  opacity: 1;
}

.ft-trust .jusprog-img {
  display: block;
  height: 31px;
  width: auto;
}

.ft-col h5 {
  font-family: var(--display);
  font-weight: 700;
  font-size: 14px;
  margin: 0 0 18px;
  letter-spacing: -0.01em;
}

.ft-col ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ft-col a {
  color: var(--ink-dim);
  font-size: 13px;
  text-decoration: none;
  transition: color 0.15s ease;
}

.ft-col a:hover {
  color: var(--pink);
}

.ft-bot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid var(--line);
  font-size: 12px;
  color: var(--ink-mute);
}

.ft-bot .legal {
  display: flex;
  gap: 20px;
}

.ft-bot .legal a {
  color: var(--ink-mute);
  text-decoration: none;
}

.ft-bot .legal a:hover {
  color: var(--pink);
}

.ft-disclaimer {
  margin: 24px 0 18px;
  padding: 18px 0 8px;
  background: transparent;
  border: none;
  font-size: 11.5px;
  line-height: 1.65;
  color: var(--ink-mute);
  text-wrap: pretty;
  opacity: 0.68;
}

.ft-disclaimer p {
  margin: 0 0 8px;
  font-weight: 400;
}

.ft-disclaimer p:last-child {
  margin-bottom: 0;
}

.ft-disclaimer a {
  color: var(--ink-mute);
  text-decoration: underline;
  text-decoration-thickness: 0.5px;
  text-underline-offset: 2px;
  font-weight: inherit;
}

.ft-disclaimer a.ft-agb-link {
  color: var(--ink-mute);
}

.ft-disclaimer a:hover {
  color: var(--ink-dim);
}

.ft-disclaimer a.ft-agb-link:hover {
  color: var(--ink-dim);
}

.ft-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 30px;
  padding: 22px 0 18px;
  border-top: 1px solid var(--line);
  flex-wrap: wrap;
}

.ft-address {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 12.5px;
  color: var(--ink-dim);
  line-height: 1.5;
}

.ft-address .pin {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: linear-gradient(135deg, var(--coral), var(--pink));
  color: #fff;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  box-shadow: 0 6px 14px -4px rgba(198, 62, 124, 0.45);
}

.ft-address .pin svg {
  width: 14px;
  height: 14px;
}

.ft-address strong {
  color: var(--ink);
  font-weight: 700;
  letter-spacing: -0.005em;
  display: block;
  margin-bottom: 1px;
}

.ft-pay {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  flex-wrap: wrap;
}

.ft-pay .pay-card {
  width: 58px;
  height: 36px;
  border-radius: 7px;
  background: #fff;
  border: 1px solid var(--line);
  display: grid;
  place-items: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 10px -4px rgba(20, 5, 15, 0.08), 0 1px 0 rgba(255, 255, 255, 0.6) inset;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.ft-pay .pay-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px -6px rgba(20, 5, 15, 0.15);
}

.ft-pay .pay-card svg {
  display: block;
}

.pay-visa {
  background: #fff;
}

.pay-visa .visa-mark {
  font-family: var(--display);
  font-weight: 800;
  font-style: italic;
  font-size: 17px;
  color: #1a1f71;
  letter-spacing: -0.04em;
  line-height: 1;
  transform: translateY(1px);
}

.pay-mc {
  background: #fff;
}

.pay-mc .mc-circles {
  position: relative;
  width: 40px;
  height: 24px;
}

.pay-mc .mc-circles span {
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.pay-mc .mc-circles span.l {
  left: 0;
  background: #eb001b;
}

.pay-mc .mc-circles span.r {
  right: 0;
  background: #f79e1b;
  mix-blend-mode: multiply;
}

.ft-pay .pay-card.pay-sofort {
  width: auto;
  background: #fff;
  padding: 6px 10px;
}

.pay-sofort img {
  display: block;
  height: 22px;
  width: auto;
}

.ft-pay .pay-card.pay-paysafe {
  width: auto;
  background: #fff;
  padding: 6px 10px;
}

.pay-paysafe img {
  display: block;
  height: 22px;
  width: auto;
}

.ft-col a.cc-reopen {
  cursor: pointer;
}

footer.is-slim .ft-trust,
footer.is-slim .ft-meta,
footer.is-legal .ft-trust,
footer.is-legal .ft-meta {
  display: none;
}

/* ===== Subpage footer overrides */
/* Subpage variant collapses the 5-track grid down to 4 (brand + 3 cols). */
.ft-top--subpage {
  grid-template-columns: 1.4fr 1fr 1fr 1fr 1fr;
}

/* Legal pages (agb / impressum / datenschutz) — viluu-site/agb.html footer */
.ft-top--legal {
  grid-template-columns: 1.4fr 1fr 1fr 1fr 1fr;
}

footer.is-slim .ft-brand p,
footer.is-legal .ft-brand p {
  margin: 0;
}

footer.is-slim .ft-disclaimer,
footer.is-legal .ft-disclaimer {
  margin: 30px 0 22px;
  padding: 22px 24px;
  background: rgba(255, 255, 255, 0.45);
  border: 1px solid var(--line);
  border-radius: 18px;
  font-size: 12px;
  line-height: 1.65;
  color: var(--ink-mute);
  text-wrap: pretty;
  opacity: 1;
}

footer.is-slim .ft-disclaimer p,
footer.is-legal .ft-disclaimer p {
  margin: 0;
}

footer.is-slim .ft-disclaimer a,
footer.is-legal .ft-disclaimer a {
  color: var(--ink-mute);
  text-decoration: underline;
  text-decoration-thickness: 0.5px;
  text-underline-offset: 2px;
  font-weight: inherit;
}

footer.is-slim .ft-disclaimer a.ft-agb-link,
footer.is-legal .ft-disclaimer a.ft-agb-link {
  color: var(--pink);
}

footer.is-slim .ft-disclaimer a:hover,
footer.is-legal .ft-disclaimer a:hover {
  color: var(--ink-dim);
}

@media (max-width: 1100px) {
  .ft-top--subpage,
  .ft-top--legal {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
}

@media (max-width: 720px) {
  .ft-trust {
    justify-content: center;
    padding-top: 18px;
  }
}

@media (max-width: 720px) {
  .ft-pay {
    gap: 6px;
    flex-wrap: nowrap;
    justify-content: flex-start;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .ft-pay::-webkit-scrollbar {
    display: none;
  }
  .ft-pay .pay-card {
    width: 42px;
    height: 26px;
    border-radius: 5px;
    flex-shrink: 0;
  }
  .ft-pay .pay-card.pay-sofort,
  .ft-pay .pay-card.pay-paysafe {
    width: auto;
    padding: 4px 6px;
  }
  .pay-sofort img,
  .pay-paysafe img {
    height: 16px;
  }
  .pay-visa .visa-mark {
    font-size: 13px;
  }
  .pay-mc .mc-circles {
    width: 28px;
    height: 18px;
  }
  .pay-mc .mc-circles span {
    width: 18px;
    height: 18px;
  }
}

@media (max-width: 700px) {
  .ft-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 18px;
  }
}

@media (max-width: 1100px) {
  .ft-top {
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }
}

@media (max-width: 900px) {
  footer {
    padding: 50px 22px 30px;
  }
}
</style>
